import { useState } from 'react'
import type { PatientResponse } from '@/api/types'
import { Avatar } from '@/components/Avatar'
import { Badge } from '@/components/Badge'
import { Icon } from '@/components/Icon'
import { LinkButton } from '@/components/LinkButton'
import { RowMenu } from '@/components/RowMenu'
import { formatAge, formatSex } from '@/lib/age'
import { formatVisitDate } from '@/lib/dates'
import { formatPhone, maskPhone } from '@/lib/phone'
import { useModals } from '@/app/modals/ModalContext'
import { useMinWidth } from '@/lib/useMediaQuery'

interface Props {
  patient: PatientResponse
  /** Undefined until the record's totals arrive — not zero, which would flash
   *  "first visit" at a patient who has been coming for years. */
  visitCount: number | undefined
}

/** Identity, always on screen: the error this prevents is writing into the
 *  wrong patient's record and not noticing. The name is the h1. */
export function PatientRecordHeader({ patient, visitCount }: Props) {
  const [phoneShown, setPhoneShown] = useState(false)
  const modals = useModals()
  // Below `lg` the record screen carries this action in its own bottom bar,
  // within reach of a thumb. Rendering it here as well put two buttons with
  // the same name a few hundred pixels apart on one phone screen.
  const wide = useMinWidth('lg')
  const returning = (visitCount ?? 0) > 1

  return (
    <div className="flex flex-wrap items-start gap-4 rounded-lg border border-line-soft bg-surface p-5">
      <Avatar name={patient.full_name} size={64} />

      <div className="min-w-0 flex-[1_1_20rem]">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl leading-tight font-bold tracking-tight">{patient.full_name}</h1>
          <Badge tone={returning ? 'success' : 'info'}>
            {returning ? 'Returning patient' : 'New patient'}
          </Badge>
        </div>

        {/* Facts on one line separated by thin rules — scannable in a glance
            rather than a stack of label/value pairs to read down. */}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-fg-muted">
          <span className="flex items-center gap-2">
            <span className="text-fg-subtle">OP No</span>
            <span className="font-medium text-fg">{patient.op_number}</span>
          </span>
          <span className="h-3.5 w-px bg-line" aria-hidden="true" />
          <span className="font-medium text-fg">{formatSex(patient.sex)}</span>
          <span className="h-3.5 w-px bg-line" aria-hidden="true" />
          <span className="font-medium text-fg">
            {formatAge(patient.age)}
            {patient.date_of_birth && (
              <span className="text-fg-subtle"> (DOB {formatVisitDate(patient.date_of_birth)})</span>
            )}
          </span>
          {patient.blood_group && (
            <>
              <span className="h-3.5 w-px bg-line" aria-hidden="true" />
              <span className="flex items-center gap-2">
                <span className="text-fg-subtle">Blood group</span>
                <span className="font-medium text-fg">{patient.blood_group}</span>
              </span>
            </>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-fg-muted [&_svg]:text-fg-subtle">
          {patient.phone && (
            <span className="flex min-w-0 items-center gap-2">
              <Icon name="phone" size="1rem" />
              {phoneShown ? formatPhone(patient.phone) : maskPhone(patient.phone)}
              {/* Masked by default: this header faces the doctor for a whole
                  consultation, in a room other people walk into. */}
              <button
                type="button"
                className="min-h-touch px-1 text-xs font-medium text-accent underline"
                onClick={() => setPhoneShown((shown) => !shown)}
              >
                {phoneShown ? 'Hide' : 'Show'}
              </button>
            </span>
          )}
          {patient.address && (
            <span className="flex min-w-0 items-center gap-2">
              <Icon name="pin" size="1rem" />
              {patient.address}
            </span>
          )}
        </div>
      </div>

      {/* The action on top, the two facts about the record underneath. On a
          phone this becomes a full-width row under the identity. */}
      <div className="ml-auto flex w-full flex-col items-stretch gap-3 md:w-auto md:items-end [&>a]:max-md:w-full">
        {wide && (
          <LinkButton to={`/patients/${patient.id}/visits/new`}>
            <Icon name="plus" />
            New consultation
          </LinkButton>
        )}
        <div className="flex w-full items-center justify-between gap-4 border-t border-line-soft pt-3 md:w-auto md:justify-start md:border-0 md:pt-0">
          <span className="flex flex-col gap-0.5 text-right whitespace-nowrap">
            <span className="text-xs text-fg-subtle">Last visit</span>
            <span className="text-sm font-semibold tabular-nums">
              {patient.last_visit_at ? formatVisitDate(patient.last_visit_at) : '—'}
            </span>
          </span>
          <span className="flex flex-col gap-0.5 border-l border-line-soft pl-4 text-right whitespace-nowrap">
            <span className="text-xs text-fg-subtle">Total visits</span>
            <span className="text-sm font-semibold tabular-nums">{visitCount ?? '—'}</span>
          </span>
          <RowMenu
            label={`Actions for ${patient.full_name}`}
            items={[
              {
                label: 'Edit patient details',
                onSelect: () => modals.open({ kind: 'patient', mode: 'edit', patientId: patient.id }),
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}
